import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import _merge from 'lodash/merge';
import Cookies from 'js-cookie';
import querystring from 'query-string';

import { submitTrackingData } from 'api/client';
import { qsParseJoinArray } from 'client/utils/queryString';
import { generateFbcByFbclid } from 'client/utils/tracking';
import { filterUndefinedOrNull } from 'utils/filter';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler.js';
import * as clientErrTypes from 'config/clientErrorTypes';

export default function useSubmitTrackingApi() {
    const trackingData = useTrackingData();
    const errHandler = useClientErrorHandler();
    return useCallback(
        async ({ promoUUID, entriesUUID, body = {} }) => {
            try {
                return await submitTrackingData({
                    promoUUID,
                    entriesUUID,
                    body: _merge({}, trackingData, body)
                });
            } catch (error) {
                console.log('Failed to submit tracking data.', error);
                errHandler({
                    err: generateApiErrObj(error, clientErrTypes.SUBMIT_TRACKING_FAILED)
                });
            }
        },
        [trackingData, errHandler]
    );
}

export function useTrackingData() {
    const { businessTrackingId } = useSelector(s => s.sponsor);
    const { utmFromParent } = useSelector(s => s.promoState);
    const [trackingData, setTrackingData] = useState(() => ({
        system: getTrackDataSystem(utmFromParent),
        biz_tracking: getTrackDataBusiness(businessTrackingId)
    }));

    useEffect(() => {
        function setGaClientId() {
            try {
                // remove ga prefix to get client id
                // GA1.1.1626056991.1692173999 -> 1626056991.1692173999
                // Ref: https://app.clickup.com/36226806/docs/12hhqp-8660/12hhqp-20080
                const gaClientId = Cookies.get('_ga').substring(6);
                if (gaClientId) setTrackingData(c => _merge({}, c, { system: { gaClientId } }));
            } catch (error) {
                console.log('Failed to get GA4 client id (_ga) from cookie:', error);
            }
        }
        function initFbCAPITracking() {
            const fbp = Cookies.get('_fbp');
            const fbc = Cookies.get('_fbc');
            const { fbclid } = querystring.parse(window.location.search);
            let trackingData = {
                clientUserAgent: navigator.userAgent
            };
            if (fbp) {
                trackingData = {
                    ...trackingData,
                    fbp
                };
            }
            if (fbclid || fbc) {
                const value = fbclid ? generateFbcByFbclid(fbclid) ?? fbc : fbc;
                if (fbclid) {
                    Cookies.set('_fbc', value);
                }
                trackingData = {
                    ...trackingData,
                    fbc: value
                };
            }
            setTrackingData(prev => {
                return {
                    ...prev,
                    biz_tracking: filterUndefinedOrNull({
                        ...prev.biz_tracking,
                        ...trackingData
                    })
                };
            });
        }
        if (isGAExist()) {
            setGaClientId();
        } else {
            setTimeout(() => setGaClientId(), 1000);
        }
        initFbCAPITracking();
    }, [utmFromParent]);

    return trackingData;
}

function isGAExist() {
    // GA 追蹤 user 的 cookie 儲存在 _ga,  一般稱為 client_id
    const gaCookie = Cookies.get('_ga');
    return gaCookie !== undefined;
}
function getTrackDataBusiness(businessTrackingId) {
    const qsObj = qsParseJoinArray(window.location.search);
    const businessTrackingIds = String(businessTrackingId)
        .split(',')
        .reduce((obj, key) => {
            key = key.trim();
            const val = qsObj[key];
            if (val) {
                obj[key] = val;
            }
            return obj;
        }, {});
    return businessTrackingIds;
}
function getTrackDataSystem(utmMergeIn = {}) {
    const qsObj = qsParseJoinArray(window.location.search);
    const {
        utm_source = '',
        utm_medium = '',
        utm_campaign = '',
        utm_term = '',
        utm_content = ''
    } = _merge({}, utmMergeIn, qsObj);
    return {
        gaClientId: '',
        utmSource: utm_source,
        utmCampaign: utm_campaign,
        utmMedium: utm_medium,
        utmTerm: utm_term,
        utmContent: utm_content,
        referralUrl: window.document.referrer
    };
}
